import { keywordsMeta } from "@/lib/seo/keywords";
// Generator för Fennys fördjupade fotosidor (tjänstenischer, prissidor och jämförelse).
// Data ligger i src/content/foto-topics.ts. Varje sida får unik metadata,
// Service-, FAQ-, ImageGallery-, Person- och AEO/GEO-schema samt bildgalleri.

import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { PhotoMasonry } from "@/components/site/foto/PhotoMasonry";
import { ReelTile } from "@/components/site/case/ReelTile";
import { coverPhotoFor, galleryPhotos, photosBy } from "@/content/photo-gallery";
import { fotoCities, PHOTOGRAPHER } from "@/content/foto";
import { fotoTopics } from "@/content/foto-topics";
import {
  aeoGeo,
  breadcrumbs,
  faqPage,
  imageGallery,
  person,
  serviceSchema,
  speakable,
} from "@/lib/seo/schema";

const CITY_NAMES = fotoCities.map((c) => c.city);

export function fotoTopicRoute(slug: string) {
  const topic = fotoTopics.find((t) => t.slug === slug);
  if (!topic) throw new Error(`Unknown foto topic ${slug}`);
  const PATH = `/foto/${topic.slug}`;
  const URL = `https://fenny.se${PATH}`;
  const gallery =
    topic.gallery === "alla" ? galleryPhotos : photosBy(topic.gallery);
  const photos = (gallery.length >= 9 ? gallery : galleryPhotos).slice(0, 14);
  const cover = coverPhotoFor(topic.slug, topic.gallery === "alla" ? undefined : topic.gallery);
  const FAQ: LandingFAQ[] = topic.faq.map((f) => ({ q: f.q, a: f.a }));

  return {
    head: () => ({
      meta: [
        ...keywordsMeta(`/foto/${topic.slug}`),
        { title: topic.metaTitle },
        { name: "description", content: topic.description },
        { property: "og:title", content: topic.metaTitle },
        { property: "og:description", content: topic.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: URL },
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            aeoGeo({
              path: PATH,
              name: topic.metaTitle,
              description: topic.description,
              question: topic.question,
              answer: topic.quickAnswer,
              speakableSelectors: ["[data-quick-answer]"],
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            serviceSchema({
              name: topic.title,
              path: PATH,
              serviceType: topic.serviceType,
              description: topic.description,
              areaServed: ["Sverige", ...CITY_NAMES],
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            imageGallery({
              name: `${topic.title} — Fenny Foto`,
              path: PATH,
              description: topic.galleryHeading,
              images: photos,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            person({
              name: PHOTOGRAPHER.name,
              jobTitle: PHOTOGRAPHER.role,
              email: "hey@fenny.se",
              path: PATH,
              description: PHOTOGRAPHER.bio,
              knowsAbout: topic.knowsAbout,
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs([
              { name: "Hem", path: "/" },
              { name: "Foto", path: "/foto" },
              { name: topic.crumb, path: PATH },
            ]),
          ),
        },
      ],
    }),
    component: function FotoTopicPage() {
      return (
        <LandingPage
          heroImage={{ src: cover.src, alt: `${topic.eyebrow} — ${cover.alt}` }}
          eyebrow={topic.eyebrow}
          title={topic.h1}
          lede={topic.lede}
          tldr={<p>{topic.quickAnswer}</p>}
          {...(topic.answerId ? { quickAnswerId: topic.answerId } : {})}
          ctaPrimary={{ label: "Begär offert", to: "#offert" }}
          ctaSecondary={{ label: "Se portfolion", to: "/foto" }}
          trackingId={`foto_${topic.slug}`}
          stills={false}
          videos={false}
          afterHero={
            <div className="py-12">
              <PhotoMasonry
                photos={photos}
                filterable={false}
                initial={9}
                eyebrow="Portfolio"
                heading={topic.galleryHeading}
              />
              {topic.featuredVideos && (
                <section className="container-wide mt-14 md:mt-20">
                  <div className="max-w-3xl">
                    <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">
                      Utvalda case
                    </div>
                    <h2 className="text-balance text-3xl font-semibold leading-[1.05] md:text-4xl">
                      {topic.featuredVideos.heading}
                    </h2>
                    {topic.featuredVideos.intro && (
                      <p className="mt-5 text-pretty text-muted-foreground md:text-lg">
                        {topic.featuredVideos.intro}
                      </p>
                    )}
                  </div>
                  <div className="mt-8 grid gap-4 md:mt-10 md:grid-cols-3 md:gap-5">
                    {topic.featuredVideos.items.map((v) => (
                      <figure key={v.youtubeId}>
                        <ReelTile
                          item={{
                            kind: "video",
                            id: v.youtubeId,
                            youtubeId: v.youtubeId,
                            title: v.title,
                          }}
                          aspect="video"
                        />
                        <figcaption className="mt-3 text-sm text-muted-foreground">
                          {v.title}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </section>
              )}
            </div>
          }
          bullets={topic.bullets}
          {...(topic.pricing
            ? {
                pricing: {
                  heading: "Prisbild 2026",
                  intro: topic.pricing.intro,
                  rows: topic.pricing.rows,
                  footnote: topic.pricing.footnote,
                },
              }
            : {})}
          {...(topic.process
            ? { process: { heading: "Så går det till", steps: topic.process } }
            : {})}
          sections={[
            ...topic.sections.map((s) => ({
              id: s.id,
              heading: s.heading,
              body: (
                <>
                  {s.paragraphs.map((p, i) => (
                    <p key={i} className={i > 0 ? "mt-4" : undefined}>
                      {p}
                    </p>
                  ))}
                </>
              ),
            })),
            {
              id: "orter",
              heading: "Var vi fotograferar",
              body: (
                <p>
                  Vi arbetar i hela Sverige med bas i Stockholm, Västerås och Malmö.
                  Vanliga orter:{" "}
                  {fotoCities.slice(0, 10).map((c, i) => (
                    <span key={c.slug}>
                      {i > 0 ? ", " : ""}
                      <a
                        className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
                        href={c.path}
                      >
                        {c.city}
                      </a>
                    </span>
                  ))}
                  .
                </p>
              ),
            },
            {
              id: "las-vidare",
              heading: "Läs vidare",
              body: (
                <p>
                  {topic.related.map((r, i) => (
                    <span key={r.to}>
                      {i > 0 ? " · " : ""}
                      <a
                        className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
                        href={r.to}
                      >
                        {r.label}
                      </a>
                    </span>
                  ))}
                </p>
              ),
            },
          ]}
          faq={FAQ}
          riskReducers
          closing={{
            title: "Ska vi räkna på ert uppdrag?",
            body: "Beskriv behovet så återkommer Fenny med upplägg och fast pris inom en arbetsdag.",
            primary: { label: "Begär offert", to: "#offert" },
            secondary: { label: "Kontakta oss", to: "/kontakt" },
          }}
          contactSlot={<FotoQuoteForm location={`foto_${topic.slug}`} />}
        />
      );
    },
  };
}
